import React, {
  useRef,
  useState,
  useMemo,
  forwardRef,
  useCallback,
} from "react";
import {
  isEmpty,
  split,
  forEach,
  isUndefined,
  map,
  isString,
  pick,
  uniq,
  flatten,
  compact,
} from "utils/lodashUtils";
import { Form, Button, Alert } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";

const integrationTypes = [
  {
    label: "Tag",
    value: "tag",
  },
  {
    label: "Device",
    value: "device",
  },
];

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ tag: undefined }],
};

const MicrosoftDefenderForEndPointMappingForm = forwardRef(
  ({ disableProjectFields = false, ...props }, ref) => {
    const [form] = Form.useForm();
    const projectRef = useRef({ device: [], tag: [] });
    const IntRef = useRef({});
    const [mappedProjectList] = useState(() => {
      if (props.record) {
        const { machineId, machineName, integrationType, tagWithMachineName } = props.record;
        const machineIdArray = split(machineId, ",");
        const machineNameArray = split(machineName, ",");
        return {
          [integrationType.toLowerCase()]: machineIdArray.map((id, index) => ({
            id,
            name: machineNameArray[index],
          })),
          tagName: tagWithMachineName?.split(':')[0],
        };
      }
      return { device: [], tag: [] };
    });
    const [type, setType] = useState();

    const [initialValues] = useState(() => {
      if (props.record) {
        return [];
      }
      return [{ ...initialFormValues }];
    });

    const fetchProjects = useCallback(
      (params) => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            ...params,
          });
        promise.then((response) => {
          const { projects } = response;
          if (isUndefined(mappedProjectList[type])) {
            mappedProjectList[type] = [];
          }
          if (
            mappedProjectList[type].length > 0 &&
            !isEmpty(response.projects) && (type === "device" || params.project === mappedProjectList.tagName)
          ) {
            response.projects = [
              ...response.projects,
              ...mappedProjectList[type],
            ];
          }
          forEach(projects, (p) => {
            projectRef.current[type][p.id] = p.name;
          });
        });
        return { promise, cancel };
      },
      [props.toolName, props.loginConfigId, mappedProjectList, type]
    );

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { integrationType, machineId, tagWithMachineId } = config;
      const parsedFields = {};
      switch (integrationType) {
        case "DEVICE":
          parsedFields.machineId = split(machineId, ",");
          parsedFields.integration = "device";
          break;
        case "TAG":
          parsedFields.integration = "tag";
          parsedFields.projectList = map(
            tagWithMachineId.split(","),
            (tagWithMachine) => {
              const [tag, ...machineId] = tagWithMachine.split(":");
              return {
                tag,
                machineId: isEmpty(machineId) ? [] : split(machineId, ";"),
              };
            }
          );

          break;
        default:
          break;
      }
      setType(parsedFields.integration);
      return parsedFields;
    }, []);

    const onIntegrationTypeChange = useCallback(
      (value, field) => {
        const { name } = field;
        setType(value);
        const configurations = form.getFieldValue("configurations") || [];
        const updatedConfiguration = [...configurations];

        updatedConfiguration[name] = {
          ...updatedConfiguration[name],
          integration: value,
          projectList: [{ tag: undefined }],
          machineId: undefined,
        };

        form.setFieldsValue({ configurations: updatedConfiguration });
      },
      [form]
    );

    const getExtraFieldsData = useCallback((config) => {
      const { machineId, integration, projectList } = config;

      const extraFieldData = {};

      switch (integration) {
        case "device":
          extraFieldData.integrationType = "DEVICE";
          extraFieldData.machineId = machineId.join();
          extraFieldData.machineName = Object.values(
            pick(projectRef.current[integration], machineId)
          ).join();
          break;
        case "tag":
          extraFieldData.integrationType = "TAG";
          extraFieldData.tagWithMachineId = map(projectList, (object) => {
            const machineIds = !isEmpty(object.machineId)
              ? `:${object.machineId.join(";")}`
              : "";
            return `${object.tag}${machineIds}`;
          }).join();
          extraFieldData.tagWithMachineName = map(projectList, (object) => {
            const names = Object.values(
              pick(projectRef.current[integration], object.machineId)
            );
            const machineNames = !isEmpty(names) ? `:${names.join(";")}` : "";
            return `${object.tag}${machineNames}`;
          }).join();

          extraFieldData.machineId = uniq(
            compact(
              flatten(
                map(projectList, (object) => {
                  const machineIds = object.machineId;
                  return machineIds;
                })
              )
            )
          ).join();
          extraFieldData.machineName = uniq(
            compact(
              flatten(
                map(projectList, (object) => {
                  const machineNames = Object.values(
                    pick(projectRef.current[integration], object.machineId)
                  );
                  return machineNames;
                })
              )
            )
          ).join();
          break;

        default:
          break;
      }
      return extraFieldData;
    }, []);

    const isProjectFieldDisabled = useCallback((fieldKey, listField, form) => {
      const fieldConfiguration =
        form.getFieldValue("configurations")[listField.name].projectList[
          fieldKey
        ]?.tag;
      return !fieldConfiguration || disableProjectFields;
    }, [disableProjectFields]);

    const projectListRender = useCallback(
      (listField) => {
        const __fields = [
          {
            label: "Tag",
            help: "Enter full tag value for filtering support",
            colSpan: 10,
            name: "tag",
            required: true,
            disabled: disableProjectFields
          },
          {
            name: "machineId",
            colSpan: "10",
            help: "Enter full device value for filtering support",
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: "Devices",
            propsGenerator: (fieldKey) => ({
              disabled: isProjectFieldDisabled(fieldKey, listField, form),
            }),
            props: {
              setExtraProps: (field, nestedListField) => {
                const allConfigs = form.getFieldsValue().configurations;
                const tagName =
                  allConfigs[listField.name]?.projectList[nestedListField.name]
                    ?.tag;
                const uniqueKey = `${listField.name}${nestedListField.name}`;
                if (
                  tagName &&
                  (!IntRef.current[uniqueKey] ||
                    tagName !== IntRef.current[uniqueKey]?.name)
                ) {
                  IntRef.current[uniqueKey] = {
                    project: tagName,
                  };
                }
                return {
                  api: !disableProjectFields && tagName && fetchProjects,
                  apiParams: IntRef.current[uniqueKey],
                  resetOptionsProp: tagName,
                  mode: "multiple",
                  allowClear: true,
                };
              },
            },
          },
          {
            name: "add",
            colSpan: 2,
            render: ({ remove, name, fields }) => {
              return (
                <Button
                  disabled={fields.length === 1}
                  type="dashed"
                  className="m-t-l"
                  onClick={() => {
                    remove(name);
                  }}
                >
                  -
                </Button>
              );
            },
          },
        ];

        const deviceFields = [
          {
            name: "machineId",
            colSpan: "10",
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: "Devices",
            help: "Enter full device value for filtering support",
            required: true,
            props: {
              api: fetchProjects,
              mode: "multiple",
              allowClear: true,
            },
          },
        ];
        const selectedType =
          form.getFieldsValue().configurations[listField.name]?.integration;
        return (
          <div className="m-b-sm">
            {selectedType === "tag" ? (
              <Form.List name={[listField.name, "projectList"]}>
                {(fields, { add, remove }) => {
                  return (
                    <div>
                      {map(fields, (field) => {
                        return generateFormFields(
                          __fields,
                          10,
                          { ...field, add, remove, fields },
                          field.name
                        );
                      })}
                      <Button
                        size="small"
                        type="dashed"
                        onClick={() => add({ tag: undefined })}
                        disabled={fields.length >= 3 || disableProjectFields}
                      >
                        + Add Tags
                      </Button>
                      <Alert
                        type="warning"
                        message="Specify tag values that are associated with at least one device"
                        className="m-t-m"
                      />
                    </div>
                  );
                }}
              </Form.List>
            ) : selectedType === "device" ? (
              generateFormFields(deviceFields, 10, listField)
            ) : null}
          </div>
        );
      },
      [isProjectFieldDisabled, fetchProjects, form, disableProjectFields]
    );

    const extraFields = useMemo(
      () => [
        {
          name: "integration",
          colSpan: "10",
          type: FieldTypes.SELECT,
          label: "Integration Type",
          options: integrationTypes,
          required: true,
          disabled: disableProjectFields,
          props: {
            onChange: onIntegrationTypeChange,
            autoFocus: !disableProjectFields
          },
        },
        {
          name: (listField) => [listField.name, "projectList"],
          render: projectListRender,
        },
      ],
      [projectListRender, onIntegrationTypeChange, disableProjectFields]
    );

    const onValuesChange = useCallback(
      (values, ...args) => {
        if (values.configurations) {
          let isProjectChanged = false;
          const formValues = form.getFieldsValue();
          forEach(values.configurations, (configs, index) => {
            //look into SnykMappingFormV2 for explanation
            if (
              !configs ||
              !configs.projectList ||
              isString(configs.projectList)
            ) {
              return;
            }
            forEach(configs.projectList, (object, i) => {
              //look into SnykMappingFormV2 for explanation
              if (object && object.tag && Object.keys(object).length === 1) {
                isProjectChanged = true;
                formValues.configurations[index].projectList[i].machineId =
                  undefined;
                formValues.configurations[index].projectList[i].integration =
                  undefined;
              }
            });
          });
          if (isProjectChanged) {
            form.setFieldsValue(formValues);
          }
        }
      },
      [form]
    );

    return (
      <GenericMappingForm
        form={form}
        ref={ref}
        getExtraFieldsData={getExtraFieldsData}
        extraFields={extraFields}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        formDefaultValues={initialValues}
        newRowDefaultValue={initialValues[0]}
        onValuesChange={onValuesChange}
        {...props}
      />
    );
  }
);

export default MicrosoftDefenderForEndPointMappingForm;
